import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Row,
  Col,
  Button,
  Form,
  Modal,
  Spinner,
} from "react-bootstrap";
import { API_URL } from "../../apiConfig";
import ToastNotification from "../ToastNotification";
import ActivationDetails from "./ActivationDetails";
import ActivationComments from "./ActivationComments";
import ActivationsReportModal from "./ActivationsReportModal";
import { useNavigate } from "react-router-dom";
import { EyeIcon, PencilIcon, TrashIcon, CheckCircleIcon, ClockIcon, BuildingOffice2Icon, BuildingStorefrontIcon, CurrencyDollarIcon, XCircleIcon, XMarkIcon, FunnelIcon, MagnifyingGlassIcon, Squares2X2Icon, ListBulletIcon, ChatBubbleLeftRightIcon, DocumentArrowDownIcon } from "@heroicons/react/24/outline";

// ── Design tokens (same as Dashboard) ─────────────────────────────────
const T = {
  bg:         "#f2f1ee",
  surface:    "rgba(255,255,255,0.72)",
  surface2:   "#f9f8f6",
  ink:        "#1A1A1C",
  ink2:       "#4A4A50",
  ink3:       "#86868B",
  ink4:       "#B4B3B0",
  hair:       "rgba(20,20,22,0.07)",
  hair2:      "rgba(20,20,22,0.04)",
  accent:     "#1e3a8a",
  accentSoft: "rgba(30,58,138,0.09)",
  ok:         "#1E8E3E",
  okSoft:     "rgba(30,142,62,0.10)",
  warn:       "#B25E09",
  warnSoft:   "rgba(178,94,9,0.10)",
  danger:     "#C5221F",
  dangerSoft: "rgba(197,34,31,0.10)",
  shadowSm:   "0 1px 2px rgba(20,20,22,0.04),0 1px 1px rgba(20,20,22,0.03)",
  shadowMd:   "0 6px 24px -8px rgba(20,20,22,0.08),0 2px 6px rgba(20,20,22,0.04)",
  font:       "-apple-system,BlinkMacSystemFont,'SF Pro Text',system-ui,sans-serif",
  mono:       "'SF Mono',ui-monospace,Menlo,monospace",
  r:          18,
};

const cardStyle     = { background: T.surface, backdropFilter: "blur(28px) saturate(180%)", WebkitBackdropFilter: "blur(28px) saturate(180%)", border: `1px solid ${T.hair}`, borderRadius: T.r, boxShadow: T.shadowMd };
const cardHeadStyle = { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 20px", borderBottom: `1px solid ${T.hair}` };
const cardTitleStyle= { fontSize: 13, fontWeight: 600, letterSpacing: "-0.005em", color: T.ink, margin: 0, display: "flex", alignItems: "center", gap: 8 };

// "Pago" = ativação ATIVADA com paidAt; continua contando como ativa
const PAID = { color: "#0F766E", bg: "rgba(15,118,110,0.10)" };

const EMPTY_STATUS_PICKER = { show: false, item: null, current: "", newStatus: "", reason: "", comment: "", saving: false };
const STATUS_OPTIONS = [
  { value: "PENDENTE", label: "Pendente",  hint: "Aguardando instalação", color: "#B25E09", bg: "rgba(178,94,9,0.10)",  Icon: ClockIcon },
  { value: "ATIVADO",  label: "Ativado",   hint: "Serviço instalado",     color: "#1E8E3E", bg: "rgba(30,142,62,0.10)", Icon: CheckCircleIcon },
  { value: "PAGO",     label: "Pago",      hint: "Comissão recebida",     color: PAID.color, bg: PAID.bg,               Icon: CurrencyDollarIcon },
  { value: "NEGATIVA", label: "Negativar", hint: "Cliente não fechou",    color: "#C5221F", bg: "rgba(197,34,31,0.10)", Icon: XCircleIcon },
];

function StatusBadge({ act }) {
  if (act.disabledAt) return (
    <span style={{ display:"inline-flex", alignItems:"center", gap:5, padding:"3px 10px", fontSize:11.5, fontWeight:500, borderRadius:999, background:T.surface2, color:T.ink3 }}>
      <span style={{ width:5, height:5, borderRadius:"50%", background:T.ink3, display:"inline-block" }} />Inativado
    </span>
  );
  const s = String(act.status||"PENDENTE").toUpperCase();
  if (s === "ATIVADO" && act.paidAt) return (
    <span title={`Pago em ${new Date(act.paidAt).toLocaleDateString("pt-BR")}${act.paidByEmail ? ` por ${act.paidByEmail.split("@")[0]}` : ""}`} style={{ display:"inline-flex", alignItems:"center", gap:5, padding:"3px 10px", fontSize:11.5, fontWeight:500, borderRadius:999, background:PAID.bg, color:PAID.color }}>
      <span style={{ width:5, height:5, borderRadius:"50%", background:PAID.color, display:"inline-block" }} />Pago
    </span>
  );
  if (s === "ATIVADO") return (
    <span style={{ display:"inline-flex", alignItems:"center", gap:5, padding:"3px 10px", fontSize:11.5, fontWeight:500, borderRadius:999, background:T.okSoft, color:T.ok }}>
      <span style={{ width:5, height:5, borderRadius:"50%", background:T.ok, display:"inline-block" }} />Ativado
    </span>
  );
  if (s === "NEGATIVA") return (
    <span style={{ display:"inline-flex", alignItems:"center", gap:5, padding:"3px 10px", fontSize:11.5, fontWeight:500, borderRadius:999, background:T.dangerSoft, color:T.danger }}>
      <span style={{ width:5, height:5, borderRadius:"50%", background:T.danger, display:"inline-block" }} />Negativa
    </span>
  );
  return (
    <span style={{ display:"inline-flex", alignItems:"center", gap:5, padding:"3px 10px", fontSize:11.5, fontWeight:500, borderRadius:999, background:T.warnSoft, color:T.warn }}>
      <span style={{ width:5, height:5, borderRadius:"50%", background:T.warn, display:"inline-block" }} />Pendente
    </span>
  );
}

const RADIUS = 20;
const FILTER_RADIUS = 10;
const filterControlStyle = { borderRadius: FILTER_RADIUS, borderColor: T.hair, fontSize: 13, fontFamily: T.font, background: T.surface2 };
const statuses = [
  { value: "ativos", label: "Ativados e Pendentes" },
  { value: "ativado", label: "Ativado" },
  { value: "pago", label: "Pago" },
  { value: "pendente", label: "Pendente" },
  { value: "inativado", label: "Inativado" },
  { value: "negativa", label: "Negativas" },
  { value: "", label: "Todos" },
];
const periods = [
  { value: "month", label: "Mês" },
  { value: "year", label: "Ano" },
  { value: "range", label: "Intervalo" },
];
const DEFAULT_LIST_LIMIT = 20;
const SHOW_MORE_LIST_LIMIT = 500;

function formatCurrency(value = 0) {
  const num = Number(value) / 100;
  return `R$ ${num.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function fmtDateTime(d) {
  if (!d) return "—";
  return new Date(d).toLocaleString("pt-BR", {
    dateStyle: "short",
    timeStyle: "short",
  });
}

function fmtDateOnly(d) {
  if (!d) return "—";
  // Se for YYYY-MM-DD, formata direto para evitar timezone
  if (typeof d === "string" && /^\d{4}-\d{2}-\d{2}$/.test(d)) {
    const [y, m, day] = d.split("-");
    return `${day}/${m}/${y}`;
  }
  return new Date(d).toLocaleDateString("pt-BR");
}

function fmtTimeOnly(d) {
  if (!d) return "—";
  return new Date(d).toLocaleTimeString("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatValueNumber(value = "") {
  return Number((value || "").toString().replace(/[^0-9]/g, "")) || 0;
}

function formatClientName(name = "") {
  if (!name) return "—";
  return name
    .toLowerCase()
    .trim()
    .split(/\s+/)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

const NEGATIVE_REASONS = [
  "Cancelado pelo Parceiro",
  "Fechou com outra empresa",
  "Já possui o serviço",
  "Mudou de endereço",
  "Não conseguiu contato",
  "Não tem interesse",
  "Outro",
  "Preço acima do esperado",
  "Problemas com documentação",
];

const STATUS_AVATAR = {
  ATIVADO:   { bg: "rgba(30,142,62,0.10)",  color: "#1E8E3E" },
  PENDENTE:  { bg: "rgba(178,94,9,0.10)",   color: "#B25E09" },
  NEGATIVA:  { bg: "rgba(197,34,31,0.10)",  color: "#C5221F" },
  INATIVADO: { bg: "rgba(20,20,22,0.06)",   color: "#86868b" },
};
function statusAvatar(item) {
  const s = String(item.status || "PENDENTE").toUpperCase();
  if (s === "NEGATIVA") return STATUS_AVATAR.NEGATIVA;
  if (item.disabledAt) return STATUS_AVATAR.INATIVADO;
  return STATUS_AVATAR[s] || STATUS_AVATAR.PENDENTE;
}
function avatarInitials(name = "") {
  return (name || "").split(" ").filter(Boolean).slice(0, 2).map(p => p[0]).join("").toUpperCase() || "?";
}
const PROV_MAP = {
  "Claro":         { c: "#E50914", l: "CL" },
  "Ligga Telecom": { c: "#7B2CBF", l: "LG" },
  "Vivo":          { c: "#660099", l: "VV" },
  "Ired Telecom":  { c: "#1a3a6b", l: "IR" },
};
function provMark(name) {
  return PROV_MAP[name] || { c: "#7B7B7B", l: (name || "??").slice(0, 2).toUpperCase() };
}

const onlyDigits = (value = "") => String(value || "").replace(/\D/g, "");
const isoDateOnly = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.valueOf())) return "";
  return date.toISOString().slice(0, 10);
};
const dayOptions = Array.from({ length: 30 }, (_, i) =>
  String(i + 1).padStart(2, "0")
);
const periodOptions = ["Manhã", "Tarde", "Noite"];
const PREVIOUS_RANGE_FLOOR = new Date(Date.UTC(2000, 0, 1));
const makeUtcDate = (
  year,
  monthIndex,
  day,
  hours = 0,
  minutes = 0,
  seconds = 0,
  ms = 0
) => new Date(Date.UTC(year, monthIndex, day, hours, minutes, seconds, ms));
const parseDateLoose = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      const [y, m, d] = trimmed.split("-").map((v) => Number(v));
      return makeUtcDate(y, m - 1, d);
    }
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(trimmed)) {
      const [d, m, y] = trimmed.split("/").map((v) => Number(v));
      return makeUtcDate(y, m - 1, d);
    }
  }
  const ms = Date.parse(value);
  if (!Number.isNaN(ms)) return new Date(ms);
  return null;
};
const filterItemsByRange = (items, startDate, endDate) => {
  if (!startDate || !endDate) return items || [];
  const startTs = startDate.getTime();
  const endTs = endDate.getTime();
  return (items || []).filter((item) => {
    const activated =
      parseDateLoose(item.activationDate) ||
      parseDateLoose(item.activationDateFormatted);
    const fallback = parseDateLoose(item.createdAt);
    const ts = (activated || fallback)?.getTime();
    if (!ts) return false;
    return ts >= startTs && ts <= endTs;
  });
};

function activationStatus(act) {
  if ((act.status || "").toUpperCase() === "NEGATIVA") return { label: "Negativa", variant: "danger", value: "NEGATIVA" };
  if (act.disabledAt) return { label: "Inativado", variant: "secondary" };
  const status = (act.status || "PENDENTE").toString();
  // Only explicit status determines classification. Default to 'PENDENTE' when missing.
  const active = status.toUpperCase() === "ATIVADO";
  if (active && act.paidAt) return { label: "Pago", variant: "success", value: "PAGO" };
  return {
    label: active ? "Ativado" : "Pendente",
    variant: active ? "success" : "warning text-dark",
    value: status
  };
}

export default function ActivationsList() {
    const [listLimit, setListLimit] = useState(DEFAULT_LIST_LIMIT);
  const [filterStatus, setFilterStatus] = useState("ativos");
  const [search, setSearch] = useState("");
  const [filterMode, setFilterMode] = useState("month");
  const [monthValue, setMonthValue] = useState(new Date().toISOString().slice(0, 7));
  const [yearValue, setYearValue] = useState(String(new Date().getFullYear()));
  const [rangeValue, setRangeValue] = useState({ start: "", end: "" });
  const [filterProvider, setFilterProvider] = useState("");
  const [data, setData] = useState({ items: [], totalMonth: 0, lastActivation: null });
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ show: false, title: "", message: "", variant: "success" });
  const [sort, setSort] = useState({ key: "activationDate", dir: "desc" });
  const [details, setDetails] = useState({ show: false, data: null });
  const navigate = useNavigate();
  const [providers, setProviders] = useState([]);
  const [activationModal, setActivationModal] = useState({
    show: false,
    mode: null,
    data: null,
  });
  const [activationForm, setActivationForm] = useState({
    activationDate: new Date().toISOString().slice(0, 10),
    contract: "", day: "", installationDate: "",
    provider: "", plan: "", value: "R$ ", period: "", notes: "",
    status: "PENDENTE", capture: "", negativeReason: "",
  });
  const [deleteModal, setDeleteModal] = useState({
    show: false,
    data: null,
    reason: "",
  });
  const [statusPickerModal, setStatusPickerModal] = useState(EMPTY_STATUS_PICKER);
  const [commentsModal, setCommentsModal] = useState({ show: false, item: null });
  const [showReport, setShowReport] = useState(false);
  const [negatives, setNegatives] = useState({ items: [], total: 0, byReason: [] });
  const [negativeEditModal, setNegativeEditModal] = useState({ show: false, item: null, reason: "", notes: "", provider: "", plan: "", saving: false });
  const [partnerColorMap, setPartnerColorMap] = useState({});
  const [previousPendingsStats, setPreviousPendingsStats] = useState({
    count: 0,
    value: 0,
    loading: false,
  });

  useEffect(() => {
    async function loadProviders() {
      try {
        const resp = await fetch(`${API_URL}/admin/partners?limit=100`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` },
        });
        const payload = await resp.json();
        if (!resp.ok) throw new Error(payload?.message || "Falha ao carregar provedores");
        const partnerItems = (payload.items || []).filter((p) => (p.role || "").toLowerCase() !== "admin");
        const names = partnerItems.map((p) => p.company).filter(Boolean).sort((a, b) => a.localeCompare(b));
        setProviders(Array.from(new Set(names)));
        const colorMap = {};
        partnerItems.forEach(p => { if (p.company && p.color) colorMap[p.company] = p.color; });
        setPartnerColorMap(colorMap);
      } catch (err) {
        showToast("Erro", err?.message || "Não foi possível carregar provedores", "danger");
      }
    }

    loadProviders();
  }, []);

  const currentFilterRange = useMemo(() => {
    let start = null;
    let end = null;
    if (filterMode === "range") {
      if (rangeValue.start) {
        const parsed = new Date(rangeValue.start);
        if (!Number.isNaN(parsed.getTime())) {
          start = makeUtcDate(parsed.getUTCFullYear(), parsed.getUTCMonth(), parsed.getUTCDate());
        }
      }
      if (rangeValue.end) {
        const parsed = new Date(rangeValue.end);
        if (!Number.isNaN(parsed.getTime())) {
          end = makeUtcDate(
            parsed.getUTCFullYear(),
            parsed.getUTCMonth(),
            parsed.getUTCDate(),
            23,
            59,
            59,
            999
          );
        }
      }
    } else if (filterMode === "month") {
      const [year, month] = monthValue.split("-");
      const yearNum = Number(year);
      const monthNum = Number(month) - 1;
      if (!Number.isNaN(yearNum) && !Number.isNaN(monthNum)) {
        start = makeUtcDate(yearNum, monthNum, 1);
        end = makeUtcDate(yearNum, monthNum + 1, 0, 23, 59, 59, 999);
      }
    } else if (filterMode === "year") {
      const yearNum = Number(yearValue);
      if (!Number.isNaN(yearNum)) {
        start = makeUtcDate(yearNum, 0, 1);
        end = makeUtcDate(yearNum, 11, 31, 23, 59, 59, 999);
      }
    }
    if (!start) {
      const now = new Date();
      start = makeUtcDate(now.getUTCFullYear(), now.getUTCMonth(), 1);
      end = makeUtcDate(now.getUTCFullYear(), now.getUTCMonth() + 1, 0, 23, 59, 59, 999);
    }
    if (!end && start) {
      end = makeUtcDate(
        start.getUTCFullYear(),
        start.getUTCMonth(),
        start.getUTCDate(),
        23,
        59,
        59,
        999
      );
    }
    return { start, end };
  }, [filterMode, monthValue, yearValue, rangeValue]);

  const previousRange = useMemo(() => {
    if (!currentFilterRange.start) return null;
    const prevStart = new Date(currentFilterRange.start);
    prevStart.setUTCMonth(prevStart.getUTCMonth() - 1);
    prevStart.setUTCDate(1);
    prevStart.setUTCHours(0, 0, 0, 0);
    const prevEnd = makeUtcDate(
      prevStart.getUTCFullYear(),
      prevStart.getUTCMonth() + 1,
      0,
      23,
      59,
      59,
      999
    );
    const earliest = new Date(PREVIOUS_RANGE_FLOOR);
    if (prevEnd < earliest) return null;
    const boundedStart = prevStart < earliest ? earliest : prevStart;
    return {
      start: boundedStart,
      end: prevEnd,
    };
  }, [currentFilterRange.start]);

  const previousRangeValue = useMemo(() => {
    if (!previousRange) return null;
    const start = isoDateOnly(previousRange.start);
    const end = isoDateOnly(previousRange.end);
    if (!start || !end) return null;
    return { start, end };
  }, [previousRange]);

  const handleShowPreviousPendings = useCallback(() => {
    if (!previousRangeValue) return;
    setFilterStatus("pendente");
    setFilterMode("range");
    setRangeValue({
      start: previousRangeValue.start,
      end: previousRangeValue.end,
    });
  }, [previousRangeValue]);

  const fetchPreviousPendingsStats = useCallback(
    async (rangeObj, rangeValueObj) => {
      if (!rangeObj || !rangeValueObj) {
        setPreviousPendingsStats({ count: 0, value: 0, loading: false });
        return;
      }
      try {
        setPreviousPendingsStats((prev) => ({ ...prev, loading: true }));
        const params = new URLSearchParams();
        params.set("status", "pendente");
        params.set("period", "range");
        params.set("startDate", rangeValueObj.start);
        params.set("endDate", rangeValueObj.end);
        params.set("limit", "500");
        const resp = await fetch(`${API_URL}/admin/activations?${params.toString()}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` },
        });
        const payload = await resp.json();
        if (!resp.ok) throw new Error(payload?.message || "Falha ao carregar pendentes anteriores");
        const items = Array.isArray(payload.items) ? payload.items : [];
    const filtered = items.filter((item) => {
      if (item.disabledAt) return false;
      const status = (item.status || "").toUpperCase();
      if (status && status !== "PENDENTE") return false;
      const activated =
        parseDateLoose(item.activationDate) ||
        parseDateLoose(item.activationDateFormatted);
      const fallback = parseDateLoose(item.createdAt);
      const ts = (activated || fallback)?.getTime();
      if (!ts) return false;
      return ts >= rangeObj.start.getTime() && ts <= rangeObj.end.getTime();
    });
    const value = filtered.reduce(
      (sum, item) => sum + formatValueNumber(item.value),
      0
    );
    setPreviousPendingsStats({ count: filtered.length, value, loading: false });
      } catch (err) {
        setPreviousPendingsStats((prev) => ({ ...prev, loading: false }));
      }
    },
    []
  );

  const rangeFilteredItems = useMemo(() => {
    return filterItemsByRange(
      data.items || [],
      currentFilterRange.start,
      currentFilterRange.end
    );
  }, [data.items, currentFilterRange]);

  const sortedItems = useMemo(() => {
    const arr = [...rangeFilteredItems];
    const dir = sort.dir === "asc" ? 1 : -1;
    arr.sort((a, b) => {
      let av = a[sort.key];
      let bv = b[sort.key];
      if (sort.key === "value") {
        av = Number((a.value || "").replace(/[^0-9]/g, ""));
        bv = Number((b.value || "").replace(/[^0-9]/g, ""));
      }
      if (sort.key === "document") {
        av = a.document || "";
        bv = b.document || "";
      }
      if (sort.key.includes("Date")) {
        av = new Date(av || a.createdAt || 0).getTime();
        bv = new Date(bv || b.createdAt || 0).getTime();
      }
      if (sort.key === "status") {
        av = activationStatus(a).label;
        bv = activationStatus(b).label;
      }
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
    return arr;
  }, [rangeFilteredItems, sort]);

  const [filteredCount, setFilteredCount] = useState(0);
  const visibleItems = useMemo(() => {
    const statusKey = (item) => {
      if (item.disabledAt) return "inativado";
      return (item.status || "pendente").toLowerCase();
    };
    const filtered = sortedItems
      .filter((item) => {
        if (filterStatus === "ativos") return statusKey(item) !== "inativado";
        if (!filterStatus) return true;
        if (filterStatus === "pago") return statusKey(item) === "ativado" && !!item.paidAt;
        return statusKey(item) === filterStatus;
      })
      .filter((item) => !filterProvider || (item.provider || "") === filterProvider);
    setFilteredCount(filtered.length);
    const limitedCount = Math.min(listLimit, filtered.length);
    return filtered.slice(0, limitedCount);
  }, [sortedItems, filterStatus, filterProvider, listLimit]);

  // Lista completa filtrada (sem limite de paginação) — usada nos totais do PDF
  const filteredAllItems = useMemo(() => {
    const statusKey = (item) => {
      if (item.disabledAt) return "inativado";
      return (item.status || "pendente").toLowerCase();
    };
    return sortedItems
      .filter((item) => {
        if (filterStatus === "ativos") return statusKey(item) !== "inativado";
        if (!filterStatus) return true;
        if (filterStatus === "pago") return statusKey(item) === "ativado" && !!item.paidAt;
        return statusKey(item) === filterStatus;
      })
      .filter((item) => !filterProvider || (item.provider || "") === filterProvider);
  }, [sortedItems, filterStatus, filterProvider]);

  const printTotalValue = useMemo(
    () => filteredAllItems.reduce((sum, item) => sum + formatValueNumber(item.value), 0),
    [filteredAllItems]
  );

  const filterLabel = useMemo(() => {
    const parts = [];
    if (filterMode === "month") parts.push(`Mês: ${monthValue}`);
    else if (filterMode === "year") parts.push(`Ano: ${yearValue}`);
    else if (filterMode === "range" && rangeValue.start && rangeValue.end)
      parts.push(`Período: ${fmtDateOnly(rangeValue.start)} a ${fmtDateOnly(rangeValue.end)}`);
    const statusLabel = statuses.find((s) => s.value === filterStatus)?.label || "Todos";
    parts.push(`Status: ${statusLabel}`);
    if (search) parts.push(`Busca: "${search}"`);
    return parts.join(" · ");
  }, [filterMode, monthValue, yearValue, rangeValue, filterStatus, search]);

  const stats = useMemo(() => {
    const items = rangeFilteredItems || [];
    const active = items.filter((a) => !a.disabledAt && (String(a.status || "").toUpperCase() === "ATIVADO"));
    const paidCount = active.filter((a) => a.paidAt).length;
    const pending = items.filter((a) => !a.disabledAt && (String(a.status || "").toUpperCase() === "PENDENTE" || !a.status));
    const formatValue = (item) => formatValueNumber(item.value);
    const totalValue = active.reduce((sum, item) => sum + formatValue(item), 0);
    const pendingValue = pending.reduce((sum, item) => sum + formatValue(item), 0);
    // Agrupa por provedora+plano — somente ativados
    const planProviderTotals = {};
    items
      .filter((item) => item.value && !item.disabledAt && String(item.status || "").toUpperCase() === "ATIVADO")
      .forEach((item) => {
        const provider = item.provider || "—";
        const plan = item.plan || "—";
        const key = provider + "|" + plan;
        if (!planProviderTotals[key]) {
          planProviderTotals[key] = { provider, plan, count: 0 };
        }
        planProviderTotals[key].count += 1;
      });
    const topByValue = Object.values(planProviderTotals)
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);
    const providerCounts = {};
    items.forEach((item) => {
      if (item.disabledAt || String(item.status || "").toUpperCase() !== "ATIVADO") return;
      const key = item.provider || "—";
      if (!providerCounts[key]) providerCounts[key] = { count: 0, value: 0 };
      providerCounts[key].count += 1;
      providerCounts[key].value += formatValue(item);
    });
    const topProviders = Object.entries(providerCounts)
      .sort(([, a], [, b]) => b.count - a.count)
      .slice(0, 5)
      .map(([provider, stats]) => ({ provider, ...stats }));
    const condoTotals = {};
    items
      .filter((item) => !item.disabledAt && String(item.status || "").toUpperCase() === "ATIVADO")
      .forEach((item) => {
        const condoKey = item.condo || item.condoName || "—";
        if (!condoTotals[condoKey]) {
          condoTotals[condoKey] = { condo: condoKey, totalValue: 0, count: 0 };
        }
        condoTotals[condoKey].totalValue += formatValue(item);
        condoTotals[condoKey].count += 1;
      });
    const topCondominios = Object.values(condoTotals)
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);
    return {
      activeCount: active.length,
      paidCount,
      pendingCount: pending.length,
      totalValue,
      pendingValue,
      topByValue,
      topProviders,
      topCondominios,
      lastActivation: data.lastActivation,
    };
  }, [rangeFilteredItems, data.lastActivation]);

  const showToast = (title, message, variant = "success") => {
    setToast({ show: true, title, message, variant });
  };

  async function fetchData() {
    if (filterStatus === "negativa") return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterMode === "range") {
        if (rangeValue.start) params.set("startDate", rangeValue.start);
        if (rangeValue.end) params.set("endDate", rangeValue.end);
        params.set("period", "range");
      } else if (filterMode === "month") {
        const [year, month] = monthValue.split("-");
        if (year && month) {
          const yearNum = Number(year);
          const monthNum = Number(month) - 1;
          if (!Number.isNaN(yearNum) && !Number.isNaN(monthNum)) {
            const start = makeUtcDate(yearNum, monthNum, 1);
            const end = makeUtcDate(yearNum, monthNum + 1, 0, 23, 59, 59, 999);
            params.set("startDate", start.toISOString());
            params.set("endDate", end.toISOString());
          }
        }
        params.set("period", "month");
      } else if (filterMode === "year") {
        const year = Number(yearValue);
        if (!Number.isNaN(year)) {
          const start = makeUtcDate(year, 0, 1);
          const end = makeUtcDate(year, 11, 31, 23, 59, 59, 999);
          params.set("startDate", start.toISOString());
          params.set("endDate", end.toISOString());
        }
        params.set("period", "year");
      }
      // When UI requests "ativos" we want server to return only ativado+pendente
      // to avoid pagination/ordering hiding matching records. Also request a larger
      // limit for admin listings so items are less likely to be pushed out of the
      // first page unintentionally.
      if (filterStatus) {
        if (filterStatus === "ativos") {
          params.set("status", "ativado,pendente,negativa");
        } else {
          params.set("status", filterStatus);
        }
      }
      // Increase default page size for admin UI to reduce missing items due to limit
      params.set("limit", "500");
      if (search) params.set("q", search);
      const resp = await fetch(`${API_URL}/admin/activations?${params.toString()}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` },
      });
      const ret = await resp.json();
      if (!resp.ok) throw new Error(ret?.message || "Falha ao carregar ativações");
      setData(ret);
      await fetchPreviousPendingsStats(previousRange, previousRangeValue);
      // Fetch negatives stats
      try {
        let negMonth = '';
        if (filterMode === 'month') negMonth = monthValue;
        else if (filterMode === 'year') negMonth = '';
        const negResp = await fetch(`${API_URL}/admin/negatives-stats${negMonth ? `?month=${negMonth}` : ''}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` },
        });
        if (negResp.ok) {
          const negData = await negResp.json();
          // Also count activations with status=NEGATIVA
          const negActivationCount = (ret.items || []).filter(a => (a.status || "").toUpperCase() === "NEGATIVA").length;
          setNegatives({ items: [], total: (negData.total || 0) + negActivationCount, byReason: negData.byReason || [] });
        }
      } catch (_) { /* silent */ }
    } catch (err) {
      showToast("Erro", err?.message || "Não foi possível carregar", "danger");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchData();
  }, [
    filterMode,
    filterStatus,
    search,
    monthValue,
    yearValue,
    rangeValue,
    previousRange,
    previousRangeValue,
    fetchPreviousPendingsStats,
  ]);

  // Fetch negatives list when filter is "negativa"
  useEffect(() => {
    if (filterStatus !== "negativa") return;
    let cancelled = false;
    async function fetchNegativesList() {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (filterMode === "month") {
          const [year, month] = monthValue.split("-");
          const yearNum = Number(year);
          const monthNum = Number(month) - 1;
          if (!Number.isNaN(yearNum) && !Number.isNaN(monthNum)) {
            params.set("startDate", new Date(Date.UTC(yearNum, monthNum, 1)).toISOString());
            params.set("endDate", new Date(Date.UTC(yearNum, monthNum + 1, 0, 23, 59, 59, 999)).toISOString());
          }
        } else if (filterMode === "year") {
          const yearNum = Number(yearValue);
          if (!Number.isNaN(yearNum)) {
            params.set("startDate", new Date(Date.UTC(yearNum, 0, 1)).toISOString());
            params.set("endDate", new Date(Date.UTC(yearNum, 11, 31, 23, 59, 59, 999)).toISOString());
          }
        } else if (filterMode === "range" && rangeValue.start && rangeValue.end) {
          params.set("startDate", new Date(rangeValue.start).toISOString());
          params.set("endDate", new Date(rangeValue.end + "T23:59:59.999Z").toISOString());
        }
        if (search) params.set("q", search);
        // Fetch pure negative documents
        const negResp = await fetch(`${API_URL}/admin/negatives?${params.toString()}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` },
        });
        const negRet = await negResp.json();
        const pureNegatives = negResp.ok ? (negRet.items || []) : [];

        // Fetch activations with status=NEGATIVA (no date range — negativas are shown regardless of period)
        const actParams = new URLSearchParams();
        actParams.set("status", "negativa");
        actParams.set("limit", "500");
        const actResp = await fetch(`${API_URL}/admin/activations?${actParams.toString()}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` },
        });
        const actRet = actResp.ok ? await actResp.json() : { items: [] };
        const activationNegativas = (actRet.items || []).map(act => ({
          _id: act._id,
          clientName: act.clientName || "",
          maskedDocument: act.maskedDocument || act.document || "",
          provider: act.provider || "",
          plan: act.plan || "",
          condo: act.condo || "",
          reason: act.negativeReason || "",
          notes: act.notes || "",
          createdAt: act.createdAt,
          _fromActivation: true,
          _activationData: act,
        }));

        if (!cancelled) {
          // Merge: pure negatives first, then activation-sourced negativas
          const merged = [
            ...pureNegatives,
            ...activationNegativas,
          ].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
          setNegatives((prev) => ({ ...prev, items: merged, total: merged.length }));
        }
      } catch (_) { /* silent */ }
      finally { if (!cancelled) setLoading(false); }
    }
    fetchNegativesList();
    return () => { cancelled = true; };
  }, [filterStatus, filterMode, monthValue, yearValue, rangeValue, search]);

  const handleSort = (key) => {
    setSort((prev) => ({
      key,
      dir: prev.key === key && prev.dir === "asc" ? "desc" : "asc",
    }));
  };

  const fillActivationForm = useCallback((record) => {
    const derivedStatus = record.status || (record.contract && record.installationDate ? "ATIVADO" : "PENDENTE");
    setActivationForm({
      activationDate: record.activationDate || isoDateOnly(record.createdAt) || new Date().toISOString().slice(0, 10),
      contract: record.contract || "", day: record.day || "",
      installationDate: record.installationDate || "",
      provider: record.provider || "", plan: record.plan || "",
      value: record.value || "R$ ", period: record.period || "",
      notes: record.notes || "", status: derivedStatus, capture: record.capture || "",
      negativeReason: record.negativeReason || "",
    });
  }, []);

  const openActivationModal = (mode, record) => {
    if (mode === "edit") {
      fillActivationForm(record);
    }
    setActivationModal({ show: true, mode, data: record });
  };

  const closeActivationModal = () => {
    setActivationModal({ show: false, mode: null, data: null });
  };

  const handleActivationFormChange = (field) => (e) => {
    let value = e?.target?.value ?? "";
    if (field === "value") {
      const digits = onlyDigits(value);
      if (!digits) {
        value = "R$ ";
      } else {
        const cents = digits.padStart(3, "0");
        const integer = cents.slice(0, -2);
        const decimal = cents.slice(-2);
        value = `R$ ${Number(integer).toLocaleString("pt-BR")},${decimal}`;
      }
    }
    setActivationForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveActivationModal = async () => {
    if (!activationModal.data) return;
    try {
      const resp = await fetch(`${API_URL}/admin/activations/${activationModal.data._id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
        },
        body: JSON.stringify({
          activationDate: activationForm.activationDate,
          contract: activationForm.contract,
          day: activationForm.day,
          installationDate: activationForm.installationDate,
          provider: activationForm.provider,
          plan: activationForm.plan,
          value: activationForm.value,
          period: activationForm.period,
          dueDate: activationForm.day,
          notes: activationForm.notes,
          status: activationForm.status,
          capture: activationForm.capture,
          negativeReason: activationForm.status === "NEGATIVA" ? activationForm.negativeReason : "",
        }),
      });
      const payload = await resp.json();
      if (!resp.ok) throw new Error(payload?.message || "Falha ao atualizar ativação");
      const updated = payload.activation;
      // If status changed to NEGATIVA and we have a reason, also register in negatives list
      if (activationForm.status === "NEGATIVA" && activationForm.negativeReason) {
        try {
          const rec = activationModal.data;
          await fetch(`${API_URL}/admin/negatives`, {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token") || ""}` },
            body: JSON.stringify({
              document: rec.document || rec.maskedDocument || "",
              clientName: rec.clientName || "",
              reason: activationForm.negativeReason,
              notes: activationForm.notes || "",
              condo: rec.condo || rec.condoName || "",
              city: rec.city || "",
              provider: rec.provider || activationForm.provider || "",
              plan: rec.plan || activationForm.plan || "",
            }),
          });
        } catch (_) { /* ignore negatives registration failure */ }
      }
      setData((prev) => ({
        ...prev,
        items: (prev.items || []).map((act) =>
          String(act._id) === String(updated._id) ? updated : act
        ),
      }));
      showToast("Ativação atualizada", "Dados alterados com sucesso.");
      closeActivationModal();
    } catch (err) {
      showToast("Erro", err?.message || "Falha ao atualizar ativação", "danger");
    }
  };

  const handleStatusChange = (item) => {
    if (!item) return;
    if (item.disabledAt && (item.status || "").toUpperCase() !== "NEGATIVA") return;
    const current = activationStatus(item).value;
    setStatusPickerModal({ ...EMPTY_STATUS_PICKER, show: true, item, current, newStatus: current });
  };

  const closeStatusPicker = () => setStatusPickerModal(EMPTY_STATUS_PICKER);

  // Salva a mudança de status e/ou o comentário numa única ação
  const handleStatusPickerSave = async () => {
    const { item, current, newStatus, reason, comment } = statusPickerModal;
    if (!item) return;
    const statusChanged = newStatus !== current;
    const text = comment.trim();
    if (!statusChanged && !text) return;
    if (statusChanged && newStatus === "NEGATIVA" && !reason) return;
    setStatusPickerModal((prev) => ({ ...prev, saving: true }));

    const authHeaders = { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token") || ""}` };
    const isActivated = String(item.status || "").toUpperCase() === "ATIVADO";
    try {
      let updated = item;
      if (statusChanged) {
        // Ativado ↔ Pago só alterna o pagamento, sem reenviar o status (preserva a data de ativação)
        const body = newStatus === "PAGO"
          ? (isActivated ? { paid: true } : { status: "ATIVADO", paid: true })
          : newStatus === "ATIVADO" && isActivated
            ? { paid: false }
            : { status: newStatus, ...(newStatus === "NEGATIVA" ? { negativeReason: reason } : {}) };
        const resp = await fetch(`${API_URL}/admin/activations/${item._id}`, { method: "PATCH", headers: authHeaders, body: JSON.stringify(body) });
        const payload = await resp.json();
        if (!resp.ok) throw new Error(payload?.message || "Falha ao atualizar status");
        updated = { ...updated, ...payload.activation };
      }
      if (text) {
        const resp = await fetch(`${API_URL}/admin/activations/${item._id}/comments`, { method: "POST", headers: authHeaders, body: JSON.stringify({ text }) });
        const payload = await resp.json();
        if (!resp.ok) throw new Error(payload?.message || "Falha ao salvar comentário");
        updated = { ...updated, comments: payload.activation.comments };
      }
      setData((prev) => ({
        ...prev,
        items: (prev.items || []).map((act) => (String(act._id) === String(updated._id) ? { ...act, ...updated } : act)),
      }));
      const label = STATUS_OPTIONS.find((o) => o.value === newStatus)?.label;
      showToast(
        statusChanged ? "Status atualizado" : "Comentário adicionado",
        statusChanged ? `Ativação alterada para ${label}${text ? ", com comentário" : ""}.` : "O comentário foi salvo na ativação."
      );
      closeStatusPicker();
    } catch (err) {
      showToast("Erro", err?.message || "Falha ao salvar", "danger");
      setStatusPickerModal((prev) => ({ ...prev, saving: false }));
    }
  };

  // Comentário salvo em qualquer modal: atualiza a lista e os modais abertos
  const handleCommentsUpdated = (updated) => {
    const same = (x) => x && String(x._id) === String(updated._id);
    setData((prev) => ({
      ...prev,
      items: (prev.items || []).map((act) => (same(act) ? { ...act, comments: updated.comments } : act)),
    }));
    setCommentsModal((prev) => (same(prev.item) ? { ...prev, item: { ...prev.item, comments: updated.comments } } : prev));
    setStatusPickerModal((prev) => (same(prev.item) ? { ...prev, item: { ...prev.item, comments: updated.comments } } : prev));
    setDetails((prev) => (same(prev.data) ? { ...prev, data: { ...prev.data, comments: updated.comments } } : prev));
  };

  const handleDeleteActivation = async () => {
    if (!deleteModal.data) return;
    try {
      const resp = await fetch(`${API_URL}/admin/activations/${deleteModal.data._id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ reason: deleteModal.reason }),
      });
      const payload = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(payload?.message || "Falha ao inativar ativação");
      const updated = payload.activation;
      setData((prev) => ({
        ...prev,
        items: (prev.items || []).map((act) =>
          String(act._id) === String(deleteModal.data._id)
            ? updated || { ...act, disabledAt: new Date().toISOString() }
            : act
        ),
      }));
      showToast("Ativação inativada", "Registro marcado como inativo.", "info");
      setDeleteModal({ show: false, data: null, reason: "" });
    } catch (err) {
      showToast("Erro", err?.message || "Falha ao inativar ativação", "danger");
    }
  };

  const handleOpenOnActivations = (item) => {
    const doc = item.document || item.maskedDocument;
    if (!doc) {
      showToast("Erro", "Documento ausente, não é possível abrir.", "danger");
      return;
    }
    navigate("/admin/ativacoes", {
      state: {
        document: doc,
        activationId: item._id,
      },
    });
  };

  return (
    <div style={{ fontFamily: T.font }}>
      <style>{`
        .filter-input::placeholder { color: #b4b3b8 !important; }
        @media screen { .print-only { display: none !important; } tbody.print-only { display: none !important; } }
        @media print {
          * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          .no-print { display: none !important; }
          .print-only { display: block !important; }
          tbody.print-only { display: table-row-group !important; }
          body { font-family: -apple-system, system-ui, sans-serif; font-size: 10px; color: #1d1d1f; background: #fff; margin: 0; padding: 0; }
          .card { box-shadow: none !important; border: 1px solid #d2d2d7 !important; border-radius: 12px !important; }
          table { border-collapse: collapse; width: 100%; }
          table thead tr { border-bottom: 1.5px solid #d2d2d7; }
          table thead th { background: #f5f5f7 !important; font-size: 8.5px; font-weight: 600; letter-spacing: 0.05em; text-transform: uppercase; color: #86868b; padding: 7px 10px; }
          table tbody td { padding: 8px 10px; font-size: 10px; color: #1d1d1f; border-bottom: 1px solid #f2f2f2; }
          table tbody tr:last-child td { border-bottom: none; }
          .table-responsive { overflow: visible !important; }
          .activation-actions { display: none !important; }
          .hover-info { opacity: 1 !important; max-height: none !important; }
          @page { margin: 14mm 16mm; }
        }
      `}</style>
      <ToastNotification
        show={toast.show}
        onClose={() => setToast((prev) => ({ ...prev, show: false }))}
        title={toast.title}
        message={toast.message}
        variant={toast.variant}
      />

      {/* ── Page head ─────────────────────────────────────────────────── */}
      <div style={{ display:"flex", alignItems:"flex-end", justifyContent:"space-between", marginBottom:20 }} className="no-print">
        <div>
          <h1 style={{ fontSize:26, fontWeight:600, letterSpacing:"-0.025em", margin:"0 0 2px", color:T.ink }}>Listar Ativações</h1>
          <div style={{ fontSize:13, color:T.ink3 }}>Exibindo {visibleItems.length} de {filteredCount} registros</div>
        </div>
        <button
          type="button"
          onClick={() => setShowReport(true)}
          style={{ display:"inline-flex", alignItems:"center", gap:7, height:38, padding:"0 16px", borderRadius:11, border:`1px solid ${T.hair}`, background:T.surface, color:T.accent, fontSize:13, fontWeight:600, fontFamily:T.font, cursor:"pointer", boxShadow:T.shadowSm }}
        >
          <DocumentArrowDownIcon style={{ width:17, height:17 }} /> Relatório PDF
        </button>
      </div>

      <ActivationsReportModal
        show={showReport}
        onHide={() => setShowReport(false)}
        partnerColorMap={partnerColorMap}
        defaultPeriod={{ mode: filterMode === "range" ? "range" : filterMode === "year" ? "year" : "month", month: monthValue, year: yearValue, start: rangeValue.start, end: rangeValue.end }}
        notify={showToast}
      />

      {/* ── KPI cards ─────────────────────────────────────────────────── */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))", gap:12, marginBottom:16 }} className="no-print">

        {/* Ativados */}
        <div style={{ ...cardStyle, padding:"16px 18px 12px", cursor:"pointer" }} onClick={() => setFilterStatus("ativado")}>
          <div style={{ fontSize:11.5, color:T.ink3, fontWeight:500, marginBottom:6 }}>Ativações ativas</div>
          <div style={{ fontSize:34, fontWeight:600, letterSpacing:"-0.04em", lineHeight:1, color:T.ink, marginBottom:6 }}>{stats.activeCount}</div>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", paddingTop:8, borderTop:`1px solid ${T.hair2}` }}>
            <span style={{ fontSize:11.5, color:T.ink3 }}>Valor total</span>
            <span style={{ fontFamily:T.mono, fontSize:12, fontWeight:600, color:T.ok }}>{formatCurrency(stats.totalValue)}</span>
          </div>
        </div>

        {/* Pendentes */}
        <div style={{ ...cardStyle, padding:"16px 18px 12px", cursor:"pointer" }} onClick={() => setFilterStatus("pendente")}>
          <div style={{ fontSize:11.5, color:T.ink3, fontWeight:500, marginBottom:6 }}>Pendentes</div>
          <div style={{ fontSize:34, fontWeight:600, letterSpacing:"-0.04em", lineHeight:1, color:T.warn, marginBottom:6 }}>{stats.pendingCount}</div>
          <div style={{ paddingTop:8, borderTop:`1px solid ${T.hair2}` }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <span style={{ fontSize:11.5, color:T.ink3 }}>Valor</span>
              <span style={{ fontFamily:T.mono, fontSize:12, fontWeight:600, color:T.warn }}>{formatCurrency(stats.pendingValue)}</span>
            </div>
            {previousRangeValue && (
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginTop:4, cursor:"pointer" }}
                onClick={(e) => { e.stopPropagation(); handleShowPreviousPendings(); }}>
                <span style={{ fontSize:11, color:T.danger }}>Pend. anteriores</span>
                <span style={{ fontFamily:T.mono, fontSize:12, fontWeight:600, color:T.danger }}>{previousPendingsStats.count}</span>
              </div>
            )}
          </div>
        </div>

        {/* Negativas */}
        <div style={{ ...cardStyle, padding:"16px 18px 12px", cursor:"pointer", border:`1px solid ${filterStatus==="negativa"?T.danger:T.hair}` }} onClick={() => setFilterStatus("negativa")}>
          <div style={{ fontSize:11.5, color:T.ink3, fontWeight:500, marginBottom:6 }}>Negativados</div>
          <div style={{ fontSize:34, fontWeight:600, letterSpacing:"-0.04em", lineHeight:1, color:T.danger, marginBottom:6 }}>{negatives.total}</div>
          {negatives.byReason.length > 0 && (
            <div style={{ paddingTop:8, borderTop:`1px solid ${T.hair2}` }}>
              {negatives.byReason.slice(0,2).map(r=>(
                <div key={r.reason} style={{ display:"flex", justifyContent:"space-between", fontSize:11, marginBottom:2 }}>
                  <span style={{ color:T.ink3, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", maxWidth:"76%" }}>{r.reason}</span>
                  <span style={{ fontFamily:T.mono, fontWeight:600, color:T.ink2 }}>{r.count}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Top condomínios */}
        <div style={{ ...cardStyle, padding:"16px 18px 12px" }}>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10 }}>
            <span style={{ fontSize:12, fontWeight:600, color:T.ink }}>Top condomínios</span>
            <BuildingOffice2Icon style={{ width:16, height:16, color:T.ink3 }} />
          </div>
          {stats.topCondominios.length ? (() => {
            const mx = stats.topCondominios[0]?.count||1;
            return <div style={{ display:"flex", flexDirection:"column", gap:6 }}>{stats.topCondominios.map((c,i)=>(
              <div key={c.condo}>
                <div style={{ display:"flex", justifyContent:"space-between", fontSize:11, marginBottom:2 }}>
                  <span style={{ color:T.ink2, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", maxWidth:"76%" }}><span style={{ color:T.ink4, marginRight:4 }}>#{i+1}</span>{c.condo}</span>
                  <span style={{ fontFamily:T.mono, color:T.ink }}>{c.count}</span>
                </div>
                <div style={{ height:3, background:T.surface2, borderRadius:999, overflow:"hidden" }}>
                  <div style={{ height:3, width:`${(c.count/mx)*100}%`, background:T.ok, borderRadius:999, transition:"width 0.3s" }} />
                </div>
              </div>
            ))}</div>;
          })() : <div style={{ fontSize:12, color:T.ink4 }}>Sem registros</div>}
        </div>

        {/* Top por ativação */}
        <div style={{ ...cardStyle, padding:"16px 18px 12px" }}>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10 }}>
            <span style={{ fontSize:12, fontWeight:600, color:T.ink }}>Top por ativação</span>
            <BuildingStorefrontIcon style={{ width:16, height:16, color:T.ink3 }} />
          </div>
          {stats.topProviders.length ? (() => {
            const mx = stats.topProviders[0]?.count||1;
            return <div style={{ display:"flex", flexDirection:"column", gap:6 }}>{stats.topProviders.map((p,i)=>(
              <div key={p.provider}>
                <div style={{ display:"flex", justifyContent:"space-between", fontSize:11, marginBottom:2 }}>
                  <span style={{ color:T.ink2, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", maxWidth:"76%" }}><span style={{ color:T.ink4, marginRight:4 }}>#{i+1}</span>{p.provider}</span>
                  <span style={{ fontFamily:T.mono, color:T.ink }}>{p.count}</span>
                </div>
                <div style={{ height:3, background:T.surface2, borderRadius:999, overflow:"hidden" }}>
                  <div style={{ height:3, width:`${(p.count/mx)*100}%`, background:T.accent, borderRadius:999, transition:"width 0.3s" }} />
                </div>
              </div>
            ))}</div>;
          })() : <div style={{ fontSize:12, color:T.ink4 }}>Sem registros</div>}
        </div>

        {/* Top por valor */}
        <div style={{ ...cardStyle, padding:"16px 18px 12px" }}>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10 }}>
            <span style={{ fontSize:12, fontWeight:600, color:T.ink }}>Top por valor</span>
            <CurrencyDollarIcon style={{ width:16, height:16, color:T.ink3 }} />
          </div>
          {stats.topByValue.length ? (() => {
            const mx = stats.topByValue[0]?.count||1;
            return <div style={{ display:"flex", flexDirection:"column", gap:6 }}>{stats.topByValue.map((p,i)=>{
              const label = `${p.provider||"—"} — ${(p.plan||"—").replace(/internet\s*/i,"").trim()}`;
              return (
                <div key={`${p.provider}-${p.plan}-${i}`}>
                  <div style={{ display:"flex", justifyContent:"space-between", fontSize:11, marginBottom:2 }}>
                    <span style={{ color:T.ink2, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", maxWidth:"76%" }}><span style={{ color:T.ink4, marginRight:4 }}>#{i+1}</span>{label}</span>
                    <span style={{ fontFamily:T.mono, color:T.ink }}>{p.count}</span>
                  </div>
                  <div style={{ height:3, background:T.surface2, borderRadius:999, overflow:"hidden" }}>
                    <div style={{ height:3, width:`${(p.count/mx)*100}%`, background:"#6c757d", borderRadius:999, transition:"width 0.3s" }} />
                  </div>
                </div>
              );
            })}</div>;
          })() : <div style={{ fontSize:12, color:T.ink4 }}>Sem registros</div>}
        </div>
      </div>

      {/* ── Filtros ────────────────────────────────────────────────────── */}
      <div style={{ ...cardStyle, padding:"16px 20px 14px", marginBottom:14 }} className="no-print">
        {/* Row 1: controls */}
        <div style={{ display:"flex", alignItems:"flex-end", gap:10, marginBottom:12, flexWrap:"wrap" }}>
          <div>
            <label style={{ fontSize:11, fontWeight:500, color:T.ink3, textTransform:"uppercase", letterSpacing:"0.04em", display:"block", marginBottom:6 }}>Modo</label>
            <Form.Select value={filterMode} onChange={(e) => setFilterMode(e.target.value)} style={{ ...filterControlStyle, minWidth:100 }}>
              {periods.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
            </Form.Select>
          </div>
          {filterMode === "month" && (
            <div>
              <label style={{ fontSize:11, fontWeight:500, color:T.ink3, textTransform:"uppercase", letterSpacing:"0.04em", display:"block", marginBottom:6 }}>Período</label>
              <Form.Control type="month" value={monthValue} onChange={(e) => setMonthValue(e.target.value)} style={filterControlStyle} />
            </div>
          )}
          {filterMode === "year" && (
            <div>
              <label style={{ fontSize:11, fontWeight:500, color:T.ink3, textTransform:"uppercase", letterSpacing:"0.04em", display:"block", marginBottom:6 }}>Período</label>
              <Form.Control type="number" min="2000" max="2100" value={yearValue} onChange={(e) => setYearValue(e.target.value)} style={{ ...filterControlStyle, minWidth:90 }} />
            </div>
          )}
          {filterMode === "range" && (
            <>
              <div>
                <label style={{ fontSize:11, fontWeight:500, color:T.ink3, textTransform:"uppercase", letterSpacing:"0.04em", display:"block", marginBottom:6 }}>Início</label>
                <Form.Control type="date" value={rangeValue.start} onChange={(e) => setRangeValue((prev) => ({ ...prev, start: e.target.value }))} style={filterControlStyle} />
              </div>
              <div>
                <label style={{ fontSize:11, fontWeight:500, color:T.ink3, textTransform:"uppercase", letterSpacing:"0.04em", display:"block", marginBottom:6 }}>Fim</label>
                <Form.Control type="date" value={rangeValue.end} onChange={(e) => setRangeValue((prev) => ({ ...prev, end: e.target.value }))} style={filterControlStyle} />
              </div>
            </>
          )}
          <div style={{ flex:1, minWidth:220 }}>
            <label style={{ fontSize:11, fontWeight:500, color:T.ink3, textTransform:"uppercase", letterSpacing:"0.04em", display:"block", marginBottom:6 }}>Buscar</label>
            <div style={{ display:"flex", alignItems:"center", gap:8, padding:"7px 12px", borderRadius:10, border:`1px solid ${T.hair}`, background:T.surface2 }}>
              <MagnifyingGlassIcon style={{ width:13, height:13, color:T.ink3, flexShrink:0 }} />
              <input
                type="text"
                className="search-bare-input"
                placeholder="Nome, plano, contrato, documento..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ flex:1, border:"none", background:"transparent", outline:"none", boxShadow:"none", fontSize:13, color:T.ink, fontFamily:T.font }}
              />
              {search && (
                <button onClick={() => setSearch("")} style={{ border:"none", background:"transparent", color:T.ink3, cursor:"pointer", display:"flex", padding:0 }}>
                  <XMarkIcon style={{ width:13, height:13 }} />
                </button>
              )}
            </div>
          </div>
          <button
            disabled={loading}
            onClick={fetchData}
            style={{ display:"inline-flex", alignItems:"center", gap:6, padding:"8px 16px", borderRadius:10, border:"none", background:T.accent, color:"#fff", fontSize:13, fontWeight:500, cursor:loading?"not-allowed":"pointer", fontFamily:T.font, opacity:loading?0.7:1, whiteSpace:"nowrap", flexShrink:0 }}
          >
            <FunnelIcon style={{ width:14, height:14 }} /> Aplicar
          </button>
        </div>
        {/* Row 2: status + provider chips */}
        <div style={{ display:"flex", gap:6, flexWrap:"wrap", alignItems:"center" }}>
          {[
            { value:"ativos",   label:"Todos",     count: stats.activeCount + stats.pendingCount + negatives.total },
            { value:"ativado",  label:"Ativados",  count: stats.activeCount },
            { value:"pago",     label:"Pagos",     count: stats.paidCount },
            { value:"pendente", label:"Pendentes", count: stats.pendingCount },
            { value:"negativa", label:"Negativas", count: negatives.total },
          ].map((chip) => {
            const on = filterStatus === chip.value;
            return (
              <button key={chip.value} onClick={() => setFilterStatus(chip.value)} style={{ display:"inline-flex", alignItems:"center", gap:5, padding:"5px 12px", borderRadius:999, fontSize:12.5, fontWeight:on?600:400, cursor:"pointer", fontFamily:T.font, border:`1px solid ${on?T.accent:T.hair}`, background:on?T.accentSoft:T.surface2, color:on?T.accent:T.ink2, transition:"all 0.12s" }}>
                {chip.label}
                <span style={{ fontSize:11, fontWeight:600, padding:"1px 6px", borderRadius:999, background:on?T.accentSoft:"rgba(20,20,22,0.07)", color:on?T.accent:T.ink3 }}>{chip.count}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Cabeçalho + totais — visível apenas na impressão */}
      <div className="print-only" style={{ marginBottom: 20, fontFamily: "-apple-system, 'SF Pro Display', system-ui, sans-serif" }}>

        {/* ── Topo: marca + título ── */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 32, height: 32, borderRadius: '50%',
              background: 'linear-gradient(135deg, #0071e3 0%, #34aadc 100%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: 'white', fontWeight: 700, fontSize: 13, letterSpacing: -0.5,
              flexShrink: 0
            }}>EP</div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#1d1d1f', letterSpacing: -0.2 }}>Euro Privilege</div>
              <div style={{ fontSize: 9, color: '#86868b', letterSpacing: 0.02 }}>Parceiros</div>
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#1d1d1f', letterSpacing: -0.3 }}>Relatório de Faturamento</div>
            <div style={{ fontSize: 9, color: '#86868b', marginTop: 2 }}>Gerado em {new Date().toLocaleString('pt-BR')}</div>
          </div>
        </div>

        {/* ── Separador ── */}
        <div style={{ borderTop: '1px solid #d2d2d7', marginBottom: 14 }} />

        {/* ── Filtro aplicado ── */}
        {filterLabel && (
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            background: '#f5f5f7', borderRadius: 20,
            padding: '4px 12px', marginBottom: 14
          }}>
            <span style={{ fontSize: 9, fontWeight: 600, color: '#86868b', letterSpacing: 0.05, textTransform: 'uppercase' }}>Filtro</span>
            <span style={{ fontSize: 9, color: '#1d1d1f' }}>{filterLabel}</span>
          </div>
        )}

        {/* ── Cards de totais ── */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
          <div style={{
            flex: 1, border: '1px solid #d2d2d7', borderRadius: 12,
            padding: '12px 16px', background: '#ffffff'
          }}>
            <div style={{ fontSize: 8.5, fontWeight: 600, color: '#86868b', letterSpacing: 0.05, textTransform: 'uppercase', marginBottom: 6 }}>Total de registros</div>
            <div style={{ fontSize: 28, fontWeight: 700, color: '#1d1d1f', letterSpacing: -1, lineHeight: 1 }}>{filteredCount}</div>
            <div style={{ fontSize: 9, color: '#86868b', marginTop: 4 }}>ativações no período</div>
          </div>
          <div style={{
            flex: 1, border: '1.5px solid #0071e3', borderRadius: 12,
            padding: '12px 16px', background: '#f0f7ff'
          }}>
            <div style={{ fontSize: 8.5, fontWeight: 600, color: '#0071e3', letterSpacing: 0.05, textTransform: 'uppercase', marginBottom: 6 }}>Valor total</div>
            <div style={{ fontSize: 28, fontWeight: 700, color: '#0071e3', letterSpacing: -1, lineHeight: 1 }}>{formatCurrency(printTotalValue)}</div>
            <div style={{ fontSize: 9, color: '#3a87cc', marginTop: 4 }}>faturamento consolidado</div>
          </div>
          <div style={{
            flex: 1, border: '1px solid #d2d2d7', borderRadius: 12,
            padding: '12px 16px', background: '#ffffff'
          }}>
            <div style={{ fontSize: 8.5, fontWeight: 600, color: '#86868b', letterSpacing: 0.05, textTransform: 'uppercase', marginBottom: 6 }}>Ticket médio</div>
            <div style={{ fontSize: 28, fontWeight: 700, color: '#1d1d1f', letterSpacing: -1, lineHeight: 1 }}>{filteredCount > 0 ? formatCurrency(printTotalValue / filteredCount) : '—'}</div>
            <div style={{ fontSize: 9, color: '#86868b', marginTop: 4 }}>por ativação</div>
          </div>
        </div>

        {/* ── Título da tabela ── */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#1d1d1f', letterSpacing: -0.2 }}>Lista de Ativações</div>
          <div style={{ fontSize: 9, color: '#86868b' }}>{filteredCount} {filteredCount === 1 ? 'registro' : 'registros'}</div>
        </div>
      </div>

      <style>{`
        .activation-row .hover-info { opacity:0; max-height:0; overflow:hidden; transition:opacity .18s,max-height .18s; display:block; }
        .activation-row:hover .hover-info { opacity:1; max-height:2.5rem; }
        .activation-actions { opacity:0; pointer-events:none; transition:opacity .18s; }
        .activation-row:hover .activation-actions { opacity:1; pointer-events:auto; }
        .prv-tbl th { text-align:left; font-weight:600; font-size:11px; color:${T.ink2}; letter-spacing:.04em; text-transform:uppercase; padding:11px 16px; border-bottom:1px solid ${T.hair}; background:transparent; cursor:pointer; }
        .prv-tbl td { padding:13px 16px; vertical-align:middle; border-bottom:1px solid ${T.hair2}; font-size:13px; color:${T.ink}; }
        .prv-tbl tr:last-child td { border-bottom:none; }
        .prv-tbl tr:hover td { background:${T.surface2}; }
      `}</style>

      {filterStatus === "negativa" ? (
        <div style={cardStyle}>
          <div style={cardHeadStyle}>
            <h3 style={cardTitleStyle}>Negativas registradas</h3>
            <span style={{ fontSize:12, color:T.ink3 }}>{negatives.items.length} registro{negatives.items.length!==1?"s":""}</span>
          </div>
          <div style={{ overflowX:"auto" }}>
            <table className="prv-tbl" style={{ width:"100%", borderCollapse:"collapse" }}>
              <thead><tr>
                <th>Cliente</th><th>Provedora</th><th>Plano</th><th>Condomínio</th><th>Motivo</th><th>Observações</th><th>Data</th><th className="no-print"></th>
              </tr></thead>
              <tbody>
                {negatives.items.map(neg=>{
                  const initials = avatarInitials(neg.clientName||"");
                  const pm = provMark(neg.provider||"");
                  return (
                  <tr key={neg._id} className="activation-row">
                    <td>
                      <div style={{ display:"flex", alignItems:"center", gap:9 }}>
                        <div style={{ width:28, height:28, borderRadius:"50%", background:"rgba(197,34,31,0.10)", color:"#C5221F", display:"grid", placeItems:"center", fontSize:10.5, fontWeight:600, flexShrink:0 }}>{initials}</div>
                        <div>
                          <div style={{ fontWeight:500 }}>{neg.clientName||"—"}</div>
                          <span className="hover-info" style={{ fontSize:11, color:T.ink3, fontFamily:T.mono }}>{neg.maskedDocument||neg.document||""}</span>
                        </div>
                      </div>
                    </td>
                    <td>
                      {neg.provider ? (
                        <div style={{ display:"flex", alignItems:"center", gap:7 }}>
                          <div style={{ width:22, height:22, borderRadius:6, background: partnerColorMap[neg.provider]||pm.c, color:"#fff", display:"grid", placeItems:"center", fontSize:9, fontWeight:700, flexShrink:0 }}>{pm.l}</div>
                          <span style={{ fontSize:13, color:T.ink2 }}>{neg.provider}</span>
                        </div>
                      ) : <span style={{ color:T.ink4 }}>—</span>}
                    </td>
                    <td style={{ color:T.ink2, fontSize:13 }}>{neg.plan||"—"}</td>
                    <td>{neg.condo||"—"}</td>
                    <td>{neg.reason ? <span style={{ display:"inline-flex", alignItems:"center", gap:5, padding:"3px 10px", fontSize:11.5, fontWeight:500, borderRadius:999, background:T.dangerSoft, color:T.danger }}>{neg.reason}</span> : <span style={{ color:T.ink3 }}>—</span>}</td>
                    <td style={{ color:T.ink3, maxWidth:180, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{neg.notes||"—"}</td>
                    <td style={{ fontFamily:T.mono, fontSize:12 }}>{fmtDateOnly(neg.createdAt)}</td>
                    <td className="no-print">
                      <div style={{ display:"flex", gap:0 }}>
                        <button style={{ border:0, background:"transparent", padding:"0 7px", cursor:"pointer", color:T.ink3 }} title="Detalhes"
                          onClick={()=>neg._fromActivation
                            ? setDetails({ show:true, data:{ ...neg._activationData, _isNegativeDoc: false } })
                            : setDetails({ show:true, data:{ ...neg, personName: neg.clientName, status:"NEGATIVA", _isNegativeDoc: true } })}>
                          <EyeIcon style={{width:15,height:15}}/>
                        </button>
                        {neg._fromActivation ? (
                          <button style={{ border:0, background:"transparent", padding:"0 7px", cursor:"pointer", color:T.danger }} title="Excluir ativação"
                            onClick={async () => {
                              if (!window.confirm("Excluir esta ativação negativa?")) return;
                              try {
                                const r = await fetch(`${API_URL}/admin/activations/${neg._id}`, { method:"DELETE", headers:{ Authorization:`Bearer ${localStorage.getItem("token")||""}`, "Content-Type":"application/json" }, body: JSON.stringify({ reason:"Excluído manualmente" }) });
                                if (!r.ok) throw new Error();
                                setNegatives(prev => ({ ...prev, items: prev.items.filter(n => n._id !== neg._id), total: prev.total - 1 }));
                                showToast("Ativação removida", "Registro excluído com sucesso.");
                              } catch { showToast("Erro", "Falha ao excluir.", "danger"); }
                            }}>
                            <TrashIcon style={{width:15,height:15}}/>
                          </button>
                        ) : <>
                        <button style={{ border:0, background:"transparent", padding:"0 7px", cursor:"pointer", color:T.accent }} title="Editar"
                          onClick={()=>setNegativeEditModal({ show:true, item:neg, reason:neg.reason||"", notes:neg.notes||"", provider:neg.provider||"", plan:neg.plan||"", saving:false })}>
                          <PencilIcon style={{width:15,height:15}}/>
                        </button>
                        <button style={{ border:0, background:"transparent", padding:"0 7px", cursor:"pointer", color:T.danger }} title="Excluir"
                          onClick={async () => {
                            if (!window.confirm("Excluir esta negativa?")) return;
                            try {
                              const r = await fetch(`${API_URL}/admin/negatives/${neg._id}`, { method:"DELETE", headers:{ Authorization:`Bearer ${localStorage.getItem("token")||""}` } });
                              if (!r.ok) throw new Error();
                              setNegatives(prev => ({ ...prev, items: prev.items.filter(n => n._id !== neg._id), total: prev.total - 1 }));
                              showToast("Negativa removida", "Registro excluído com sucesso.");
                            } catch { showToast("Erro", "Falha ao excluir negativa.", "danger"); }
                          }}>
                          <TrashIcon style={{width:15,height:15}}/>
                        </button>
                        </>}
                      </div>
                    </td>
                  </tr>
                  );
                })}
                {!negatives.items.length&&<tr><td colSpan={8} style={{ textAlign:"center", color:T.ink4, padding:32 }}>Nenhuma negativa registrada no período.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div style={cardStyle}>
          <div style={cardHeadStyle} className="no-print">
            <h3 style={cardTitleStyle}>Lista de Ativações</h3>
            <div style={{ display:"flex", alignItems:"center", gap:8 }}>
              <span style={{ fontSize:12.5, color:T.ink3, fontWeight:500 }}>{filteredCount} resultado{filteredCount!==1?"s":""}</span>
              <div style={{ display:"flex", gap:2, background:T.hair2, borderRadius:8, padding:2 }}>
                <button style={{ display:"inline-flex", alignItems:"center", justifyContent:"center", width:28, height:28, borderRadius:6, border:"none", background:T.surface, boxShadow:"0 1px 3px rgba(20,20,22,0.08)", cursor:"default", color:T.ink2 }} title="Lista"><ListBulletIcon style={{ width:14, height:14 }}/></button>
                <button style={{ display:"inline-flex", alignItems:"center", justifyContent:"center", width:28, height:28, borderRadius:6, border:"none", background:"transparent", cursor:"default", color:T.ink4 }} title="Grade"><Squares2X2Icon style={{ width:14, height:14 }}/></button>
              </div>
            </div>
          </div>
          <div style={{ overflowX:"auto" }}>
            <table className="prv-tbl no-print" style={{ width:"100%", borderCollapse:"collapse" }}>
              <thead><tr>
                <th style={{ width:"20%" }} onClick={()=>handleSort("clientName")}>Cliente</th>
                <th style={{ width:"12%" }} onClick={()=>handleSort("provider")}>Provedora</th>
                <th style={{ width:"18%" }} onClick={()=>handleSort("plan")}>Plano</th>
                <th style={{ width:"14%" }}>Condomínio</th>
                <th style={{ width:"8%" }} onClick={()=>handleSort("value")}>Valor</th>
                <th style={{ width:"8%" }} onClick={()=>handleSort("activationDate")}>Ativação</th>
                <th style={{ width:"8%" }} onClick={()=>handleSort("installationDate")}>Instalação</th>
                <th style={{ width:"8%" }} onClick={()=>handleSort("status")}>Status</th>
                <th style={{ width:"4%" }} className="no-print">Ações</th>
              </tr></thead>
              <tbody className="no-print">
                {visibleItems.map((item, idx)=>{
                  const clientName = formatClientName(item.personName||item.clientName||"");
                  const doc = item.document||item.maskedDocument;
                  const actDate = item.activationDate||item.createdAt;
                  const providerName = item.provider||"";
                  const condo = item.condo||item.condoName||"—";
                  const av = statusAvatar(item);
                  const pm = provMark(providerName);
                  return (
                    <tr key={item._id} className="activation-row">
                      <td>
                        <div style={{ display:"flex", alignItems:"center", gap:9 }}>
                          <div style={{ width:28, height:28, borderRadius:"50%", background:av.bg, color:av.color, display:"grid", placeItems:"center", fontSize:10.5, fontWeight:600, flexShrink:0 }}>
                            {avatarInitials(clientName)}
                          </div>
                          <div>
                            <span role="button" style={{ fontWeight:500, cursor:doc?"pointer":"default", color:T.ink, display:"block", lineHeight:1.3 }} onClick={()=>handleOpenOnActivations(item)} title="Abrir tela de ativação">{clientName||"—"}</span>
                            <span className="hover-info" style={{ fontSize:11, color:T.ink3, fontFamily:T.mono }}>{doc||""}</span>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                          <div style={{ width:24, height:24, borderRadius:7, background: partnerColorMap[providerName] || pm.c, color:"#fff", display:"grid", placeItems:"center", fontSize:10, fontWeight:700, flexShrink:0 }}>
                            {pm.l}
                          </div>
                          <span style={{ color:T.ink2, fontSize:13 }}>{providerName||"—"}</span>
                        </div>
                      </td>
                      <td>
                        <span style={{ color:T.ink }}>{item.plan||"—"}</span>
                        <span className="hover-info" style={{ fontSize:11.5, color:T.ink3 }}>Contrato: {item.contract||"—"}</span>
                      </td>
                      <td style={{ color:T.ink2 }}>{condo}</td>
                      <td style={{ fontFamily:T.mono, fontSize:12.5, fontWeight:600, color:T.ink }}>{formatCurrency(formatValueNumber(item.value))}</td>
                      <td>
                        <div style={{ fontFamily:T.mono, fontSize:12 }}>{fmtDateOnly(actDate)}</div>
                        <span className="hover-info" style={{ fontSize:11.5, color:T.ink3 }}>{item.createdAt?fmtTimeOnly(item.createdAt):"—"}</span>
                      </td>
                      <td>
                        <div style={{ fontFamily:T.mono, fontSize:12 }}>{fmtDateOnly(item.installationDate)||"—"}</div>
                      </td>
                      <td>
                        <span style={{ cursor:(!item.disabledAt||(item.status||'').toUpperCase()==='NEGATIVA')?"pointer":"default", userSelect:"none" }}
                          onClick={()=>handleStatusChange(item)}
                          title={(!item.disabledAt||(item.status||'').toUpperCase()==='NEGATIVA')?"Clique para alterar status":"Ativação inativada"}>
                          <StatusBadge act={item} />
                        </span>
                      </td>
                      <td className="no-print">
                        <div className="activation-actions" style={{ display:"flex", gap:0 }}>
                          <button style={{ border:0, background:"transparent", padding:"0 8px", cursor:"pointer", color:T.ink3 }} onClick={()=>setDetails({show:true,data:item})} title="Detalhes"><EyeIcon style={{width:16,height:16}}/></button>
                          {(!item.disabledAt||(item.status||'').toUpperCase()==='NEGATIVA')&&<>
                            <button style={{ border:0, background:"transparent", padding:"0 8px", cursor:"pointer", color:T.accent }} onClick={()=>openActivationModal("edit",item)} title="Editar"><PencilIcon style={{width:16,height:16}}/></button>
                            <button style={{ border:0, background:"transparent", padding:"0 8px", cursor:"pointer", color:T.danger }} onClick={()=>setDeleteModal({show:true,data:item,reason:""})} title="Inativar"><TrashIcon style={{width:16,height:16}}/></button>
                          </>}
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {!sortedItems.length&&<tr><td colSpan={8} style={{ textAlign:"center", color:T.ink4, padding:32 }}>Nenhuma ativação encontrada.</td></tr>}
              </tbody>
              <tbody className="print-only">
                {filteredAllItems.map((item,idx)=>{
                  const clientName = formatClientName(item.personName||item.clientName||"");
                  const actDate = item.activationDate||item.createdAt;
                  const st = activationStatus(item);
                  return (
                    <tr key={item._id} style={{ borderBottom:"1px solid #f2f2f2", background:idx%2===0?"#fff":"#fafafa" }}>
                      <td><div style={{fontWeight:500}}>{clientName}</div><div style={{fontSize:"0.85em",color:"#86868b"}}>{item.document||item.maskedDocument||"—"}</div></td>
                      <td>{item.provider||"—"}</td>
                      <td><div>{item.plan||"—"}</div><div style={{fontSize:"0.85em",color:"#86868b"}}>Contrato: {item.contract||"—"}</div></td>
                      <td>{formatCurrency(formatValueNumber(item.value))}</td>
                      <td>{fmtDateOnly(actDate)}</td>
                      <td>{fmtDateOnly(item.installationDate)}</td>
                      <td><span style={{display:"inline-block",padding:"2px 8px",borderRadius:20,fontSize:"0.85em",fontWeight:600,background:st.variant==="success"?"#e8f5e9":st.variant==="warning"?"#fff8e1":"#f3f3f6",color:st.variant==="success"?"#2e7d32":st.variant==="warning"?"#f57f17":"#86868b"}}>{st.label}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {(filteredCount > DEFAULT_LIST_LIMIT || listLimit > DEFAULT_LIST_LIMIT) && (
            <div style={{ display:"flex", justifyContent:"center", padding:"14px 20px", borderTop:`1px solid ${T.hair2}` }} className="no-print">
              {listLimit <= DEFAULT_LIST_LIMIT ? (
                <button disabled={loading} style={{ padding:"8px 16px", borderRadius:10, border:`1px solid ${T.hair}`, background:T.surface, fontSize:13, fontWeight:500, cursor:loading?"not-allowed":"pointer", color:T.ink2, fontFamily:T.font }} onClick={()=>setListLimit(SHOW_MORE_LIST_LIMIT)}>Mostrar mais</button>
              ) : (
                <button disabled={loading} style={{ padding:"8px 16px", borderRadius:10, border:`1px solid ${T.hair}`, background:T.surface, fontSize:13, fontWeight:500, cursor:loading?"not-allowed":"pointer", color:T.ink2, fontFamily:T.font }} onClick={()=>setListLimit(DEFAULT_LIST_LIMIT)}>Mostrar menos</button>
              )}
            </div>
          )}
        </div>
      )}

      <Modal show={activationModal.show && activationModal.mode === "edit"} onHide={closeActivationModal} centered size="lg">
        <Modal.Header closeButton style={{ borderBottom: `1px solid ${T.hair}`, padding: "18px 24px" }}>
          <Modal.Title style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: T.accentSoft, color: T.accent, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <PencilIcon style={{ width: 18, height: 18 }} />
            </div>
            <div>
              <div style={{ fontSize: 16, fontWeight: 600, letterSpacing: "-0.01em", color: T.ink }}>Alterar ativação</div>
              <div style={{ fontSize: 12, color: T.ink3, marginTop: 1 }}>
                {activationModal.data?.clientName || activationModal.data?.personName || "—"}
              </div>
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: 20 }}>

          {/* Status atual */}
          <div style={{ padding: "10px 16px", background: "rgba(20,20,22,0.03)", borderRadius: 12, border: `1px solid ${T.hair}` }}>
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: T.ink3, textTransform: "uppercase", letterSpacing: "0.07em", flexShrink: 0 }}>Status atual</span>
            <div style={{ display: "flex", gap: 6 }}>
              {[{ value: "PENDENTE", label: "Pendente" }, { value: "ATIVADO", label: "Ativado" }, { value: "NEGATIVA", label: "Negativa" }].map(({ value, label }) => {
                const active = activationForm.status === value;
                return (
                  <button key={value} type="button" onClick={() => setActivationForm(prev => ({ ...prev, status: value }))}
                    style={{ border: active ? "none" : `1px solid ${T.hair}`, background: active ? T.ink : "transparent", color: active ? "#fff" : T.ink2, borderRadius: 999, padding: "4px 14px", fontSize: 12.5, fontWeight: active ? 600 : 400, cursor: "pointer", fontFamily: T.font }}>
                    {label}
                  </button>
                );
              })}
            </div>
            </div>
            {activationForm.status === "NEGATIVA" && (
              <div style={{ marginTop: 14 }}>
                <div style={{ fontSize: 10.5, fontWeight: 600, color: T.ink3, textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 8 }}>Motivo *</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
                  {NEGATIVE_REASONS.map((r) => {
                    const active = activationForm.negativeReason === r;
                    return (
                      <button key={r} type="button"
                        onClick={() => setActivationForm(prev => ({ ...prev, negativeReason: r }))}
                        style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 12px", borderRadius: 10, fontSize: 13, fontWeight: active ? 500 : 400, cursor: "pointer", fontFamily: T.font, textAlign: "left", border: `1px solid ${active ? "#C5221F" : T.hair}`, background: active ? "rgba(197,34,31,0.10)" : T.surface2, color: active ? "#C5221F" : T.ink2 }}>
                        <span style={{ width: 14, height: 14, borderRadius: "50%", flexShrink: 0, border: `2px solid ${active ? "#C5221F" : T.ink3}`, background: active ? "#C5221F" : "transparent", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                          {active && <span style={{ width: 5, height: 5, borderRadius: "50%", background: "#fff", display: "block" }} />}
                        </span>
                        {r}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* IDENTIFICAÇÃO */}
          <div>
            <div style={{ fontSize: 10.5, fontWeight: 700, color: T.ink3, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>Identificação</div>
            <Row className="g-3">
              <Col md={4}>
                <Form.Label style={{ fontSize: 10.5, fontWeight: 600, color: T.ink3, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 5 }}>Data da ativação *</Form.Label>
                <Form.Control style={{ borderRadius: 10, borderColor: T.hair, fontSize: 13 }} type="date" value={activationForm.activationDate} onChange={handleActivationFormChange("activationDate")} />
              </Col>
              <Col md={4}>
                <Form.Label style={{ fontSize: 10.5, fontWeight: 600, color: T.ink3, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 5 }}>Contrato</Form.Label>
                <Form.Control style={{ borderRadius: 10, borderColor: T.hair, fontSize: 13 }} value={activationForm.contract} onChange={handleActivationFormChange("contract")} placeholder="Nº do contrato" />
              </Col>
              <Col md={4}>
                <Form.Label style={{ fontSize: 10.5, fontWeight: 600, color: T.ink3, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 5 }}>Dia do vencimento</Form.Label>
                <Form.Select style={{ borderRadius: 10, borderColor: T.hair, fontSize: 13 }} value={activationForm.day} onChange={handleActivationFormChange("day")}>
                  <option value="">Selecione</option>
                  {dayOptions.map((d) => <option key={d} value={d}>{d}</option>)}
                </Form.Select>
              </Col>
            </Row>
          </div>

          {/* PLANO CONTRATADO */}
          <div>
            <div style={{ fontSize: 10.5, fontWeight: 700, color: T.ink3, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>Plano contratado</div>
            <Row className="g-3">
              <Col md={4}>
                <Form.Label style={{ fontSize: 10.5, fontWeight: 600, color: T.ink3, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 5 }}>Provedora *</Form.Label>
                <Form.Select style={{ borderRadius: 10, borderColor: T.hair, fontSize: 13 }} value={activationForm.provider} onChange={handleActivationFormChange("provider")}>
                  <option value="">Selecione</option>
                  {providers.map((name) => <option key={name} value={name}>{name}</option>)}
                </Form.Select>
              </Col>
              <Col md={4}>
                <Form.Label style={{ fontSize: 10.5, fontWeight: 600, color: T.ink3, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 5 }}>Plano *</Form.Label>
                <Form.Control style={{ borderRadius: 10, borderColor: T.hair, fontSize: 13 }} value={activationForm.plan} onChange={handleActivationFormChange("plan")} placeholder="Ex: Internet Fibra 500 MB" />
              </Col>
              <Col md={4}>
                <Form.Label style={{ fontSize: 10.5, fontWeight: 600, color: T.ink3, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 5 }}>Valor mensal *</Form.Label>
                <div style={{ position: "relative" }}>
                  <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", fontSize: 13, color: T.ink3, fontWeight: 500, pointerEvents: "none", zIndex: 1 }}>R$</span>
                  <Form.Control style={{ borderRadius: 10, borderColor: T.hair, fontSize: 13, paddingLeft: 34 }} value={activationForm.value.replace(/^R\$\s*/, "")} onChange={handleActivationFormChange("value")} placeholder="0,00" />
                </div>
              </Col>
            </Row>
          </div>

          {/* INSTALAÇÃO */}
          <div>
            <div style={{ fontSize: 10.5, fontWeight: 700, color: T.ink3, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>Instalação</div>
            <Row className="g-3">
              <Col md={6}>
                <Form.Label style={{ fontSize: 10.5, fontWeight: 600, color: T.ink3, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 5 }}>Período</Form.Label>
                <Form.Select style={{ borderRadius: 10, borderColor: T.hair, fontSize: 13 }} value={activationForm.period} onChange={handleActivationFormChange("period")}>
                  <option value="">Selecione</option>
                  {periodOptions.map((p) => <option key={p} value={p}>{p}</option>)}
                </Form.Select>
              </Col>
              <Col md={6}>
                <Form.Label style={{ fontSize: 10.5, fontWeight: 600, color: T.ink3, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 5 }}>Data de instalação</Form.Label>
                <Form.Control style={{ borderRadius: 10, borderColor: T.hair, fontSize: 13 }} type="date" value={activationForm.installationDate} onChange={handleActivationFormChange("installationDate")} />
              </Col>
            </Row>
          </div>

          {/* CAPTAÇÃO */}
          <div>
            <div style={{ fontSize: 10.5, fontWeight: 700, color: T.ink3, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>Captação</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {["Mudança", "Indicação", "Síndico", "Tela Indoor", "Campanha", "Outros"].map((opt) => {
                const active = activationForm.capture === opt;
                return (
                  <button key={opt} type="button" onClick={() => setActivationForm(prev => ({ ...prev, capture: active ? "" : opt }))}
                    style={{ border: `1px solid ${active ? T.accent : T.hair}`, background: active ? T.accentSoft : "transparent", color: active ? T.accent : T.ink2, borderRadius: 999, padding: "5px 15px", fontSize: 13, fontWeight: active ? 600 : 400, cursor: "pointer", fontFamily: T.font }}>
                    {opt}
                  </button>
                );
              })}
            </div>
          </div>

          {/* NOTAS INTERNAS */}
          <div>
            <div style={{ fontSize: 10.5, fontWeight: 700, color: T.ink3, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>Notas internas</div>
            <Form.Control style={{ borderRadius: 10, borderColor: T.hair, fontSize: 13, resize: "vertical" }} as="textarea" rows={3} value={activationForm.notes} onChange={handleActivationFormChange("notes")} placeholder="Anotações visíveis apenas para o time interno..." />
          </div>

        </Modal.Body>
        <Modal.Footer style={{ borderTop: `1px solid ${T.hair}`, padding: "14px 24px", gap: 8 }}>
          <Button variant="outline-secondary" style={{ borderRadius: 10, fontSize: 13, borderColor: T.hair, color: T.ink2 }} onClick={closeActivationModal}>Cancelar</Button>
          <Button style={{ borderRadius: 10, fontSize: 13, fontWeight: 500, background: T.accent, borderColor: T.accent, display: "inline-flex", alignItems: "center", gap: 6 }} onClick={handleSaveActivationModal}>
            <CheckCircleIcon style={{ width: 15, height: 15 }} /> Salvar alterações
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={deleteModal.show}
        onHide={() => setDeleteModal({ show: false, data: null, reason: "" })}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Inativar ativação</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-danger fw-semibold">
            Esta ação não poderá ser desfeita.
          </p>
          <p>
            Confirma inativar a ativação de
            <br />
            <strong>
              {deleteModal.data?.provider || "—"} - {deleteModal.data?.plan || "—"}
            </strong>
            ?
          </p>
          <Form.Label>Motivo</Form.Label>
          <Form.Control
            as="textarea"
            rows={2}
            value={deleteModal.reason}
            onChange={(e) =>
              setDeleteModal((prev) => ({ ...prev, reason: e.target.value }))
            }
          />
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => setDeleteModal({ show: false, data: null, reason: "" })}
          >
            Cancelar
          </Button>
          <Button variant="danger" onClick={handleDeleteActivation}>
            Confirmar
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={details.show}
        onHide={() => setDetails({ show: false, data: null })}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span style={{ fontSize: 16, fontWeight: 600, letterSpacing: "-0.01em" }}>Detalhes da Ativação</span>
            {details.data && <StatusBadge act={details.data} />}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {details.data ? (
            <ActivationDetails
              data={details.data}
              fmtDateTime={fmtDateTime}
              clientNameFallback={details.data.personName}
              documentFallback={details.data.document}
              phoneFallback={details.data.phone}
              mobileFallback={details.data.mobile}
              onCommentsUpdated={handleCommentsUpdated}
            />
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setDetails({ show: false, data: null })}
          >
            Fechar
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ── Negative Edit Modal ── */}
      <Modal show={negativeEditModal.show} onHide={()=>setNegativeEditModal(p=>({...p,show:false}))} centered>
        <Modal.Header closeButton style={{ borderBottom:`1px solid ${T.hair}`, padding:"16px 20px" }}>
          <Modal.Title style={{ fontSize:15, fontWeight:600 }}>Editar negativa</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding:"18px 20px", display:"flex", flexDirection:"column", gap:14 }}>
          <Row className="g-2" style={{ marginBottom:2 }}>
            <Col md={6}>
              <div style={{ fontSize:10.5, fontWeight:600, color:T.ink3, textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:5 }}>Provedora</div>
              <Form.Select value={negativeEditModal.provider} onChange={e=>setNegativeEditModal(p=>({...p,provider:e.target.value}))}
                style={{ borderRadius:10, borderColor:T.hair, fontSize:13 }}>
                <option value="">Selecione</option>
                {providers.map(name=><option key={name} value={name}>{name}</option>)}
              </Form.Select>
            </Col>
            <Col md={6}>
              <div style={{ fontSize:10.5, fontWeight:600, color:T.ink3, textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:5 }}>Plano</div>
              <Form.Control value={negativeEditModal.plan} onChange={e=>setNegativeEditModal(p=>({...p,plan:e.target.value}))}
                placeholder="Ex: Internet Fibra 500 MB" style={{ borderRadius:10, borderColor:T.hair, fontSize:13 }}/>
            </Col>
          </Row>
          <div>
            <div style={{ fontSize:10.5, fontWeight:600, color:T.ink3, textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:8 }}>Motivo *</div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:6 }}>
              {NEGATIVE_REASONS.map(r=>{
                const active = negativeEditModal.reason===r;
                return (
                  <button key={r} type="button" onClick={()=>setNegativeEditModal(p=>({...p,reason:r}))}
                    style={{ display:"flex", alignItems:"center", gap:8, padding:"8px 10px", borderRadius:10, fontSize:12.5, fontWeight:active?500:400, cursor:"pointer", fontFamily:T.font, textAlign:"left", border:`1px solid ${active?"#C5221F":T.hair}`, background:active?"rgba(197,34,31,0.10)":T.surface2, color:active?"#C5221F":T.ink2 }}>
                    <span style={{ width:13, height:13, borderRadius:"50%", flexShrink:0, border:`2px solid ${active?"#C5221F":T.ink3}`, background:active?"#C5221F":"transparent", display:"inline-flex", alignItems:"center", justifyContent:"center" }}>
                      {active&&<span style={{ width:4, height:4, borderRadius:"50%", background:"#fff", display:"block" }}/>}
                    </span>
                    {r}
                  </button>
                );
              })}
            </div>
          </div>
          <Form.Control as="textarea" rows={3} placeholder="Observações (opcional)" value={negativeEditModal.notes}
            onChange={e=>setNegativeEditModal(p=>({...p,notes:e.target.value}))}
            style={{ borderRadius:10, borderColor:T.hair, fontSize:13 }}/>
        </Modal.Body>
        <Modal.Footer style={{ borderTop:`1px solid ${T.hair}`, padding:"12px 20px", gap:8 }}>
          <Button variant="outline-secondary" style={{ borderRadius:10, fontSize:13 }} onClick={()=>setNegativeEditModal(p=>({...p,show:false}))}>Cancelar</Button>
          <Button disabled={!negativeEditModal.reason||negativeEditModal.saving} onClick={async()=>{
            setNegativeEditModal(p=>({...p,saving:true}));
            try {
              const r = await fetch(`${API_URL}/admin/negatives/${negativeEditModal.item?._id}`,{
                method:"PATCH", headers:{"Content-Type":"application/json", Authorization:`Bearer ${localStorage.getItem("token")||""}`},
                body:JSON.stringify({ reason:negativeEditModal.reason, notes:negativeEditModal.notes, provider:negativeEditModal.provider, plan:negativeEditModal.plan }),
              });
              if(!r.ok) throw new Error();
              const saved = await r.json();
              setNegatives(prev=>({...prev, items:prev.items.map(n=>n._id===saved._id?saved:n)}));
              showToast("Negativa atualizada","Motivo alterado com sucesso.");
              setNegativeEditModal(p=>({...p,show:false,saving:false}));
            } catch { showToast("Erro","Falha ao salvar.","danger"); setNegativeEditModal(p=>({...p,saving:false})); }
          }} style={{ borderRadius:10, fontSize:13, fontWeight:500, background:"#C5221F", borderColor:"#C5221F", color:"#fff", display:"inline-flex", alignItems:"center", gap:6 }}>
            {negativeEditModal.saving?<Spinner size="sm" animation="border"/>:"Salvar"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ── Comentários da ativação ── */}
      <Modal show={commentsModal.show} onHide={() => setCommentsModal({ show: false, item: null })} centered>
        <Modal.Header closeButton style={{ borderBottom: `1px solid ${T.hair}`, padding: "16px 20px" }}>
          <Modal.Title style={{ fontSize: 15, fontWeight: 600, display: "flex", flexDirection: "column", gap: 2 }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}><ChatBubbleLeftRightIcon style={{ width: 18, height: 18, color: T.accent }} /> Comentários</span>
            {commentsModal.item && (
              <span style={{ fontSize: 12, fontWeight: 400, color: T.ink3 }}>
                {formatClientName(commentsModal.item.personName || commentsModal.item.clientName || "")} · {commentsModal.item.provider || "—"} · {commentsModal.item.condo || "—"}
              </span>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: "16px 20px" }}>
          {commentsModal.item && (
            <ActivationComments activation={commentsModal.item} onUpdated={handleCommentsUpdated} maxHeight={340} autoFocus />
          )}
        </Modal.Body>
      </Modal>

      {/* ── Status Picker Modal ── */}
      {(() => {
        const sp = statusPickerModal;
        const item = sp.item;
        const currentOpt = STATUS_OPTIONS.find((o) => o.value === sp.current);
        const selected = STATUS_OPTIONS.find((o) => o.value === sp.newStatus) || currentOpt;
        const statusChanged = sp.newStatus !== sp.current;
        const hasComment = !!sp.comment.trim();
        const needsReason = statusChanged && sp.newStatus === "NEGATIVA" && !sp.reason;
        const canSave = !sp.saving && (statusChanged || hasComment) && !needsReason;
        const actionLabel = statusChanged
          ? (sp.newStatus === "NEGATIVA" ? "Registrar negativa" : `Marcar como ${selected?.label}`)
          : "Salvar comentário";
        const actionColor = statusChanged ? selected?.color : T.accent;
        const prevComments = item?.comments || [];
        return (
          <Modal show={sp.show} onHide={closeStatusPicker} centered size="lg">
            <Modal.Header closeButton style={{ borderBottom: `1px solid ${T.hair}`, padding: "16px 20px", alignItems: "flex-start" }}>
              <div style={{ minWidth: 0 }}>
                <Modal.Title style={{ fontSize: 15, fontWeight: 600 }}>Alterar status</Modal.Title>
                {item && (
                  <div style={{ fontSize: 12, color: T.ink3, marginTop: 3 }}>
                    <span style={{ color: T.ink2, fontWeight: 500 }}>{formatClientName(item.personName || item.clientName || "")}</span>
                    {" · "}{item.provider || "—"}{item.condo ? ` · ${item.condo}` : ""}
                  </div>
                )}
              </div>
            </Modal.Header>
            <Modal.Body style={{ padding: "18px 20px", display: "flex", flexDirection: "column", gap: 18 }}>
              <div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                  <span style={{ fontSize: 10.5, fontWeight: 600, color: T.ink3, textTransform: "uppercase", letterSpacing: "0.07em" }}>Status</span>
                  {currentOpt && (
                    <span style={{ fontSize: 11.5, color: T.ink3 }}>
                      Atual: <strong style={{ color: currentOpt.color, fontWeight: 600 }}>{currentOpt.label === "Negativar" ? "Negativa" : currentOpt.label}</strong>
                    </span>
                  )}
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 8 }}>
                  {STATUS_OPTIONS.map(({ value, label, hint, color, bg, Icon }) => {
                    const active = sp.newStatus === value;
                    const isCurrent = sp.current === value;
                    return (
                      <button key={value} type="button"
                        onClick={() => setStatusPickerModal((prev) => ({ ...prev, newStatus: value, reason: value === "NEGATIVA" ? prev.reason : "" }))}
                        style={{ position: "relative", display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 6, padding: "11px 12px", borderRadius: 12, cursor: "pointer", fontFamily: T.font, textAlign: "left", border: `1.5px solid ${active ? color : T.hair}`, background: active ? bg : "#fff", boxShadow: active ? "none" : T.shadowSm, transition: "all .12s" }}>
                        <Icon style={{ width: 18, height: 18, color: active ? color : T.ink3 }} />
                        <span style={{ fontSize: 13, fontWeight: 600, color: active ? color : T.ink }}>{label}</span>
                        <span style={{ fontSize: 11, color: active ? color : T.ink3, opacity: active ? 0.85 : 1, lineHeight: 1.3 }}>{hint}</span>
                        {isCurrent && (
                          <span style={{ position: "absolute", top: 8, right: 8, fontSize: 9.5, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", padding: "1px 6px", borderRadius: 999, background: T.surface2, color: T.ink3, border: `1px solid ${T.hair}` }}>atual</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {statusChanged && sp.newStatus === "NEGATIVA" && (
                <div>
                  <div style={{ fontSize: 10.5, fontWeight: 600, color: T.ink3, textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 8 }}>Motivo da negativa *</div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
                    {NEGATIVE_REASONS.map((r) => {
                      const active = sp.reason === r;
                      return (
                        <button key={r} type="button"
                          onClick={() => setStatusPickerModal((prev) => ({ ...prev, reason: r }))}
                          style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", borderRadius: 10, fontSize: 12, fontWeight: active ? 500 : 400, cursor: "pointer", fontFamily: T.font, textAlign: "left", border: `1px solid ${active ? "#C5221F" : T.hair}`, background: active ? "rgba(197,34,31,0.10)" : T.surface2, color: active ? "#C5221F" : T.ink2 }}>
                          <span style={{ width: 13, height: 13, borderRadius: "50%", flexShrink: 0, border: `2px solid ${active ? "#C5221F" : T.ink3}`, background: active ? "#C5221F" : "transparent", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                            {active && <span style={{ width: 5, height: 5, borderRadius: "50%", background: "#fff", display: "block" }} />}
                          </span>
                          {r}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 10.5, fontWeight: 600, color: T.ink3, textTransform: "uppercase", letterSpacing: "0.07em" }}>
                    <ChatBubbleLeftRightIcon style={{ width: 13, height: 13 }} /> Comentário
                    <span style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>(opcional)</span>
                  </span>
                  {prevComments.length > 0 && (
                    <button type="button" onClick={() => { closeStatusPicker(); setCommentsModal({ show: true, item }); }} style={{ background: "none", border: "none", padding: 0, fontSize: 11.5, color: T.accent, cursor: "pointer", fontFamily: T.font }}>
                      Ver todos ({prevComments.length})
                    </button>
                  )}
                </div>
                {prevComments.slice(-2).map((c) => (
                  <div key={c._id} style={{ fontSize: 12, color: T.ink2, padding: "6px 10px", marginBottom: 6, borderLeft: `2px solid ${T.hair}`, background: T.surface2, borderRadius: "0 8px 8px 0" }}>
                    <span style={{ color: T.ink3 }}><strong style={{ color: T.ink2, fontWeight: 600 }}>{(c.authorEmail || "").split("@")[0]}</strong> · {fmtDateTime(c.createdAt)}</span>
                    <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", marginTop: 2 }}>{c.text}</div>
                  </div>
                ))}
                <Form.Control
                  as="textarea"
                  rows={2}
                  maxLength={2000}
                  placeholder={statusChanged ? "Explique a mudança, se quiser…" : "Adicionar um comentário…"}
                  value={sp.comment}
                  onChange={(e) => setStatusPickerModal((prev) => ({ ...prev, comment: e.target.value }))}
                  onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey) && canSave) handleStatusPickerSave(); }}
                  style={{ borderRadius: 10, borderColor: T.hair, fontSize: 13, resize: "vertical" }}
                />
              </div>
            </Modal.Body>
            <Modal.Footer style={{ borderTop: `1px solid ${T.hair}`, padding: "12px 20px", justifyContent: "space-between" }}>
              <span style={{ fontSize: 11.5, color: needsReason ? "#C5221F" : T.ink4 }}>
                {needsReason ? "Escolha o motivo da negativa" : !statusChanged && !hasComment ? "Escolha um novo status ou escreva um comentário" : "Ctrl/⌘ + Enter para salvar"}
              </span>
              <div style={{ display: "flex", gap: 8 }}>
                <Button variant="outline-secondary" style={{ borderRadius: 10, fontSize: 13 }} onClick={closeStatusPicker}>Cancelar</Button>
                <Button
                  disabled={!canSave}
                  onClick={handleStatusPickerSave}
                  style={{ borderRadius: 10, fontSize: 13, fontWeight: 500, background: actionColor, borderColor: "transparent", color: "#fff", display: "inline-flex", alignItems: "center", gap: 6, opacity: canSave ? 1 : 0.45 }}>
                  {sp.saving ? <Spinner size="sm" animation="border" /> : actionLabel}
                </Button>
              </div>
            </Modal.Footer>
          </Modal>
        );
      })()}

    </div>
  );
}
